import { FetchAPI } from '../Main/fetchAPI';
import { CLIENT_INSURANCE_AUTHORIZATION, INSURANCE_AUTHORIZATION } from '../constants';
import {
	AddAuthorizationProperties,
	AuthorizationProperties,
	GetAllocatedHoursDetailProperties,
	GetAllocationPageDataProperties,
	GetAuthorizationReportProperties,
	GetAvailableHoursProperties,
	GetCalculatedHoursRangeProperties,
	GetInsuranceAuthorizationsProperties,
	GetUtilizationReportProperties,
	UpdateAllocatedHoursProperties,
	UpdateInsuranceAuthorization,
	UserAllocatedHoursDetailProperties,
} from './insuranceAuthorizationProperties';

export const InsuranceAuthorization = {
	getInsuranceAuthorizationTypes: () =>
		FetchAPI.getJson({
			urlStr: `${INSURANCE_AUTHORIZATION}/types`,
		}),
	getUsersAllocationData: ({ userIds, clientId }: { clientId: string; userIds: string[] }) => {
		let ids = '';

		if (userIds.length) {
			ids = userIds.join('&userIds=');
		}

		return FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/allocated-hours?${userIds.length ? `&userIds=${ids}` : ''}`,
		});
	},
	getUtilizationReport: (data: GetUtilizationReportProperties) => {
		const { endDate, startDate, clientIds, providerIds, roleSections, authorizationSessionTypeId } = data;
		const query = new URLSearchParams();
		let ids = '';
		let userIds = '';
		let roleIds = '';
		let sessionTypeIds = '';

		if (clientIds.length) {
			ids = clientIds.join('&clientIds=');
		}

		if (providerIds?.length) {
			userIds = providerIds.join('&providerIds=');
		}

		if (roleSections?.length) {
			roleIds = roleSections.join('&roleSections=');
		}

		if (authorizationSessionTypeId?.length) {
			sessionTypeIds = authorizationSessionTypeId.join('&authorizationSessionTypeId=');
		}
		if (startDate) query.set('startDate', startDate);
		if (endDate) query.set('endDate', endDate);

		return FetchAPI.getJson({
			urlStr: `${INSURANCE_AUTHORIZATION}/utilization-report/v2?${query.toString()}${
				clientIds.length ? `&clientIds=${ids}` : ''
			}${providerIds?.length ? `&providerIds=${userIds}` : ''}${
				roleSections?.length ? `&roleSections=${roleIds}` : ''
			}${authorizationSessionTypeId?.length ? `&authorizationSessionTypeId=${sessionTypeIds}` : ''}`,
		});
	},
	getAuthorizationReport: (data: GetAuthorizationReportProperties) => {
		const { clientIds, authorizationSessionTypeId } = data;
		let ids = '';
		let types = '';

		if (clientIds.length) {
			ids = clientIds.join('&clientIds=');
		}

		if (authorizationSessionTypeId.length) {
			types = authorizationSessionTypeId.join('&authorizationSessionTypeId=');
		}

		return FetchAPI.getJson({
			urlStr: `${INSURANCE_AUTHORIZATION}/authorization-report?${
				clientIds.length ? `&clientIds=${ids}` : ''
			}${authorizationSessionTypeId.length ? `&authorizationSessionTypeId=${types}` : ''}`,
		});
	},
	getInsuranceAuthorizationSessionTypes: (type?: string) =>
		FetchAPI.getJson({
			urlStr: `${INSURANCE_AUTHORIZATION}/session-types${type ? `?authorizationType=${type}` : ''}`,
		}),
	getInsuranceAuthorizationPerWeekData: (data: GetCalculatedHoursRangeProperties) => {
		const { endDate, startDate, totalHours } = data;
		const query = new URLSearchParams();

		if (startDate) query.set('startDate', startDate);
		if (endDate) query.set('endDate', endDate);
		if (totalHours) query.set('totalHours', `${totalHours}`);

		return FetchAPI.getJson({
			urlStr: `${INSURANCE_AUTHORIZATION}/hours-per-week?${query.toString()}`,
		});
	},
	getActiveInsuranceAuthorizations: ({ clientId, userId }: { clientId: string; userId?: string }) =>
		FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/active${userId ? `?userId=${userId}` : ''}`,
		}),
	getInsuranceAuthorizations: (data: GetInsuranceAuthorizationsProperties) => {
		const { clientId, page, pageSize } = data;
		const query = new URLSearchParams();
		if (page) query.set('page', page);
		if (pageSize) query.set('pageSize', pageSize);

		return FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}?${query.toString()}`,
		});
	},
	saveInsuranceAuthorizations: (clientId: string, data: AddAuthorizationProperties) =>
		FetchAPI.postJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}`,
			data,
		}),
	saveAssessmentInsuranceAuthorizations: (clientId: string, data: AuthorizationProperties) =>
		FetchAPI.postJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/assessment`,
			data,
		}),
	saveTreatmentAuthorizations: (clientId: string, data: AuthorizationProperties) =>
		FetchAPI.postJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/treatment`,
			data,
		}),
	getAvailableHours: ({ clientId, authorizationId }: GetAvailableHoursProperties) =>
		FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/${authorizationId}/available-hours`,
		}),
	getAllocatedHoursDetailsUser: ({ clientId, authorizationId, userId }: UserAllocatedHoursDetailProperties) =>
		FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/${authorizationId}/allocated-hours/${userId}`,
		}),
	updateAllocatedHours: (data: UpdateAllocatedHoursProperties) => {
		const { clientId, authorizationId } = data;

		return FetchAPI.postJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/${authorizationId}/allocate`,
			data,
		});
	},
	getAllocatedHoursDetails: ({ clientId, authorizationId, authorizationDetailId }: GetAllocatedHoursDetailProperties) =>
		FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(
				clientId,
			)}/${authorizationId}/details/${authorizationDetailId}/allocated-hours`,
		}),
	getUserUtilizationDetails: ({
		clientId,
		authorizationId,
		authorizationDetailId,
	}: GetAllocatedHoursDetailProperties) =>
		FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(
				clientId,
			)}/${authorizationId}/details/${authorizationDetailId}/utilization-by-provider`,
		}),
	editAuthorization: (data: UpdateInsuranceAuthorization) =>
		FetchAPI.putJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(data.clientId)}/${data.authorizationId}`,
			data,
		}),
	deleteAuthorization: (data: GetAvailableHoursProperties) =>
		FetchAPI.deleteJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(data.clientId)}/${data.authorizationId}`,
		}),
	getAllocationPageDataRbt: ({ clientId, includeExpired }: GetAllocationPageDataProperties) => {
		const query = new URLSearchParams();
		if (typeof includeExpired === 'boolean') query.set('includeExpired', `${includeExpired}`);
		const queryString = query.toString();

		return FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/allocation-page-data/rbt${
				queryString ? `?${queryString}` : ''
			}`,
		});
	},
	getAllocationPageDataBcba: ({ clientId, includeExpired }: GetAllocationPageDataProperties) => {
		const query = new URLSearchParams();
		if (typeof includeExpired === 'boolean') query.set('includeExpired', `${includeExpired}`);
		const queryString = query.toString();

		return FetchAPI.getJson({
			urlStr: `${CLIENT_INSURANCE_AUTHORIZATION(clientId)}/allocation-page-data/bcba${
				queryString ? `?${query.toString()}` : ''
			}`,
		});
	},
};
